<template>
    <m-pageframe title="Import Consent">
        <v-container fluid style="padding: 15px">
            <v-card-title>
                <v-btn icon @click="goBackToImportSearch" style="margin-right: 10px;" title="Back to Import Search">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-icon left>mdi-file-upload</v-icon>
                Import Consent Records
            </v-card-title>

            <v-card-text>
                <!-- Read-Only Mode Alert -->
                <v-alert v-if="is_read_only_mode" type="info" style="margin-bottom: 20px;">
                    <div><strong>Read-Only Access</strong></div>
                    <div v-if="$rc.$state.isAuthActorSystemAdmin()">
                        As RedChirp staff, you can view consent imports but cannot create or modify imports in
                        production organizations.
                    </div>
                    <div v-else>
                        You have read-only access to consent imports. Only organization administrators can create or
                        modify imports.
                    </div>
                </v-alert>

                <!-- Wizard Stepper -->
                <div class="import-stepper-wrapper" style="position: relative;">
                    <v-stepper v-model="current_step" alt-labels style="margin-bottom: 20px;">
                    <v-stepper-header>
                        <v-stepper-step :complete="current_step > 1" step="1" :editable="canEditStep(1)"
                            @click="goToStep(1)">
                            Upload File
                            <small v-if="import_meta_data">{{ import_meta_data.original_file_name }}</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step :complete="current_step > 2" step="2" :editable="canEditStep(2)"
                            @click="goToStep(2)">
                            Column Mapping
                            <small>Map your columns</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step :complete="current_step > 3" step="3" :editable="canEditStep(3)"
                            @click="goToStep(3)">
                            Review
                            <small v-if="preview_data">{{ preview_data.total_records || 0 }} records</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step :complete="current_step > 4" step="4" :editable="canEditStep(4)"
                            @click="goToStep(4)">
                            Legal Attestation
                            <small>Required for import</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step step="5" :editable="false">
                            Import Complete
                        </v-stepper-step>
                    </v-stepper-header>

                    <v-stepper-items>

                        <!-- Step 1: Upload File -->
                        <v-stepper-content step="1">

                            <div style="max-width: 500px; margin: 0 auto;" v-if="should_show_loading_screen">
                                <div style="margin-bottom: 8px; font-weight: 500;">
                                    Loading Import...
                                </div>
                                <v-progress-linear indeterminate color="primary" height="12" rounded>
                                </v-progress-linear>
                                <div style="margin-top: 8px; font-size: 14px; color: #666;">
                                    Please wait while we load your import.
                                </div>
                            </div>
                            <div v-else-if="!uploading ">
                                <div style="margin-bottom: 20px;">
                                    <h3>Instructions</h3>
                                    <p>
                                        Use this tool to import consent records from an Excel file. The consent import
                                        process allows you to
                                        bulk upload customer consent preferences for conversational, informational, and
                                        promotional messages.
                                    </p>

                                    <p>
                                        <strong>Important:</strong> There is a re-mapping step but the closer you can
                                        get your file to match the format
                                        below, the easier the import will be. Below is a template you can use to get
                                        started.
                                    </p>

                                    <div style="margin: 15px 0;">
                                        <v-btn color="secondary" v-on:click="onClickDownloadTemplate" outlined>
                                            <v-icon left>mdi-download</v-icon>
                                            Download Template File
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Field Requirements Section -->
                                <div style="margin-bottom: 20px;">
                                    <h3 style="margin-bottom: 10px;">
                                        Excel File Format Requirements
                                    </h3>
                                    <p style="margin-bottom: 15px; color: #666;">
                                        Your Excel file must contain columns with data for all the required columns
                                        below and at least one of the consent columns.
                                    </p>

                                    <v-card outlined>
                                        <v-simple-table>
                                            <template v-slot:default>
                                                <thead>
                                                    <tr style="background-color: #f5f5f5;">
                                                        <th class="text-left" style="font-weight: 600;">Column Name</th>
                                                        <th class="text-left" style="font-weight: 600;">Required</th>
                                                        <th class="text-left" style="font-weight: 600;">Format</th>
                                                        <th class="text-left" style="font-weight: 600;">Example</th>
                                                        <th class="text-left" style="font-weight: 600;">Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td><strong>Full Name</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>John Smith</td>
                                                        <td>Customer's full name</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Phone Number</strong></td>
                                                        <td>
                                                            <v-chip small color="red"
                                                                text-color="white">Required</v-chip>
                                                        </td>
                                                        <td>Phone format</td>
                                                        <td>555-123-4567<br />(555) 123-4567</td>
                                                        <td>US phone numbers in any standard format</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Consent Date</strong></td>
                                                        <td>
                                                            <v-chip small color="red"
                                                                text-color="white">Required</v-chip>
                                                        </td>
                                                        <td>YYYY-MM-DD or MM/DD/YYYY</td>
                                                        <td>2025-07-18<br />07/18/2025</td>
                                                        <td>Date when consent was given</td>
                                                    </tr>

                                                    <tr>
                                                        <td><strong>Conversational</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>grant/deny/blank</td>
                                                        <td>grant, deny, true, false, yes, no</td>
                                                        <td>Consent for conversational messages</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Informational</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>grant/deny/blank</td>
                                                        <td>grant, deny, true, false, yes, no</td>
                                                        <td>Consent for informational messages</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Promotional</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>grant/deny/blank</td>
                                                        <td>grant, deny, true, false, yes, no</td>
                                                        <td>Consent for promotional messages</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Specimen</strong></td>
                                                        <td>
                                                            <v-chip small color="red"
                                                                text-color="white">Required</v-chip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>Customer provided consent via website</td>
                                                        <td>Description of how consent was obtained</td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </v-card>

                                </div>

                                <v-divider style="margin: 20px 0;"></v-divider>

                                <!-- File Upload Section -->
                                <div v-if="has_write_access && (!import_id || import_state === 'uploaded')">
                                    <h4>Upload Excel File</h4>

                                    <v-file-input v-model="excel_file" show-size accept=".xlsx"
                                        label="Excel File (XLSX)" style="padding-bottom: 15px; max-width: 500px;"
                                        :rules="file_rules" :disabled="uploading" />

                                    <div style="margin-bottom: 20px;">
                                        <v-btn color="primary" v-on:click="onClickUploadFile"
                                            :disabled="!can_upload_file" :loading="uploading">
                                            <v-icon left>mdi-upload</v-icon>
                                            Upload File
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- Upload Progress -->
                            <ImportProcessProgress v-if="uploading" title="Uploading File..."
                                description="Please wait while we upload and process your consent file."
                                :process-id="upload_process_id" import-type="consent"
                                :organization-id="$route.params.organization_id"
                                default-status-message="Uploading your file..." @complete="onUploadComplete"
                                @failed="onUploadFailed" />

                            <!-- No Write Access Message -->
                            <div v-if="!has_write_access && (!import_id || import_state === 'uploaded')">
                                <v-alert type="warning" style="margin-bottom: 20px;">
                                    <div><strong>Upload Not Available</strong></div>
                                    <div v-if="$rc.$state.isAuthActorSystemAdmin()">
                                        RedChirp staff can only upload consent imports in test/demo organizations.
                                    </div>
                                    <div v-else>
                                        Only organization administrators can upload consent imports.
                                    </div>
                                </v-alert>
                            </div>

                            <!-- Upload Errors -->
                            <div v-if="upload_errors.length > 0">
                                <v-alert type="error" style="margin-bottom: 20px;">
                                    <div><strong>Upload Errors:</strong></div>
                                    <ul>
                                        <li v-for="error in upload_errors" :key="error">{{ error }}</li>
                                    </ul>
                                </v-alert>
                            </div>
                        </v-stepper-content>

                        <!-- Step 2: Column Mapping -->
                        <v-stepper-content step="2">
                            <div v-if="import_state === 'uploaded' && sheet_information">
                                <ColumnMappingComponent :available-sheets="sheet_information.available_sheets"
                                    :sheet-metadata-map="sheet_metadata_map"
                                    :required-fields="requiredFields" :optional-fields="optionalFields"
                                    :importType="import_type" :suggested-mapping="suggestedMapping"
                                    :sample-data="sheet_information.available_sheets" :saving="saving_column_mapping"
                                    @mapping-changed="onColumnMappingChanged"
                                    @can-continue-changed="onCanContinueChanged"
                                    @continue-to-preview="onContinueToPreview"
                                    @sheet-changed="onColumnMappingSheetChanged" />

                                <!-- Intermediate Sheet Creation Progress -->
                                <ImportProcessProgress v-if="saving_column_mapping && intermediate_process_id"
                                    title="Creating Preview..."
                                    description="Please wait while we process your column mapping and create a preview."
                                    :process-id="intermediate_process_id" :import-type=import_type
                                    :organization-id="$route.params.organization_id"
                                    default-status-message="Processing column mapping..."
                                    @complete="onIntermediateComplete" @failed="onIntermediateFailed" />

                                <!-- Step 2 Navigation -->
                                <v-card-actions v-if="!loading_sheet_information" style="padding: 0; margin-top: 20px;">
                                    <v-spacer></v-spacer>

                                    <v-btn color="primary" @click="onContinueToPreview"
                                        :disabled="!canContinueToPreview || saving_column_mapping"
                                        :loading="saving_column_mapping">
                                        Continue to Preview
                                        <v-icon right>mdi-arrow-right</v-icon>
                                    </v-btn>
                                </v-card-actions>
                            </div>

                            <div v-else-if="loading_sheet_information" style="text-align: center; padding: 40px 0;">
                                <h3 style="margin-bottom: 20px;">
                                    Loading Sheet Information...
                                </h3>

                                <!-- Loading Progress -->
                                <div style="max-width: 500px; margin: 0 auto;">
                                    <div style="margin-bottom: 8px; font-weight: 500;">
                                        Analyzing your Excel file structure
                                    </div>
                                    <v-progress-linear indeterminate color="primary" height="12" rounded>
                                    </v-progress-linear>
                                    <div style="margin-top: 8px; font-size: 14px; color: #666;">
                                        Please wait while we prepare the column mapping
                                    </div>
                                </div>
                            </div>

                            <div v-else>
                                <v-alert type="error" style="margin-bottom: 20px;"
                                    v-if="intermediate_process_errors.length > 0">
                                    <div v-for="error in intermediate_process_errors" :key="error">
                                        {{ error }}. Please try again.
                                    </div>

                                </v-alert>

                            </div>
                        </v-stepper-content>

                        <!-- Step 3: Review Preview -->
                        <v-stepper-content step="3">

                            <!-- Preview Generation Loading -->
                           <div style="max-width: 500px; margin: 0 auto;" v-if="generating_preview">
                                <div style="margin-bottom: 8px; font-weight: 500;">
                                    Generating Preview...
                                </div>
                                <v-progress-linear indeterminate color="primary" height="12" rounded>
                                </v-progress-linear>
                                <div style="margin-top: 8px; font-size: 14px; color: #666;">
                                    Please wait while we prepare your preview
                                </div>
                            </div>

                            <!-- Preview Generation Failed -->
                            <div v-else-if="preview_failed">
                                <v-alert type="error" style="margin-bottom: 20px;">
                                    <div><strong>Preview Generation Failed</strong></div>
                                    <div>{{ preview_error_message }}</div>
                                </v-alert>

                                <v-btn color="primary" v-on:click="startPreviewGeneration"
                                    :loading="generating_preview">
                                    <v-icon left>mdi-refresh</v-icon>
                                    Retry Preview Generation
                                </v-btn>
                            </div>

                            <!-- Preview Ready -->
                            <div v-else-if="preview_ready">
                                <!-- Failed import message -->

                                <!-- Cancelled import message -->
                                <div v-if="import_state === 'cancelled'" style="margin-bottom: 20px;">
                                    <v-alert type="warning" style="margin-bottom: 15px;">
                                        <div
                                            style="display: flex; align-items: center; justify-content: space-between;">
                                            <div>
                                                <div><strong>Import Cancelled</strong></div>
                                                <div>This import has been cancelled and cannot be continued. You can
                                                    view the preview below but
                                                    cannot proceed to legal attestation.</div>
                                            </div>
                                            <v-btn color="white" outlined style="margin-left: 20px;"
                                                v-on:click="startNewUploadInNewTab">
                                                <v-icon left>mdi-upload</v-icon>
                                                Start New Upload
                                            </v-btn>
                                        </div>
                                    </v-alert>
                                </div>
                                <div v-else-if="has_errors_preventing_import" style="margin-bottom: 20px;">
                                    <v-alert type="error" style="margin-bottom: 15px;">
                                        <div
                                            style="display: flex; align-items: center; justify-content: space-between;">
                                            <div>
                                                <div><strong>Cannot proceed with import due to validation
                                                        errors.</strong>
                                                </div>
                                                <div v-if="preview_data">
                                                    {{ preview_data.total_records -
                                                        preview_data.valid_records_count }} of
                                                    {{ preview_data.total_records }} rows have errors.
                                                    Please fix the errors below and re-upload the file to continue.
                                                </div>
                                                <div v-else>
                                                    Please fix the errors below and re-upload the file to continue.
                                                </div>
                                            </div>
                                            <v-btn color="white" outlined style="margin-left: 20px;"
                                                v-on:click="startNewUploadInNewTab">
                                                <v-icon left>mdi-upload</v-icon>
                                                Start New Upload
                                            </v-btn>
                                        </div>
                                    </v-alert>
                                </div>
                                <div v-else-if="import_state === 'failed'" style="margin-bottom: 20px;">
                                    <v-alert type="error" style="margin-bottom: 15px;">
                                        <div
                                            style="display: flex; align-items: center; justify-content: space-between;">
                                            <div>
                                                <div><strong>Unexpected Import Error</strong></div>
                                                <div>Please retry with a new import.</div>
                                            </div>
                                            <v-btn color="white" outlined style="margin-left: 20px;"
                                                v-on:click="startNewUploadInNewTab">
                                                <v-icon left>mdi-upload</v-icon>
                                                Start New Upload
                                            </v-btn>
                                        </div>
                                    </v-alert>
                                </div>
                                <div v-else style="margin-bottom: 20px;">
                                    <v-alert type="success" outlined dense class="mb-4">
                                        <div class="d-flex align-center">
                                            <div>
                                                Successfully generated preview, review the below preview and proceed
                                                to legal attestation when ready.
                                            </div>

                                        </div>
                                    </v-alert>
                                </div>

                                <!-- Import Details -->
                                <div v-if="preview_data && preview_data.import_definition" class="import-details"
                                    style="margin-bottom: 20px;">
                                    <v-card class="elevation-1" style="padding: 16px;">
                                        <h4 class="mb-2">Import Details</h4>
                                        <v-card outlined class="mb-4">
                                            <v-card-text class="py-2 d-flex align-center">
                                                <v-icon class="ml-1 mr-3">mdi-file-excel</v-icon>
                                                <div class="text-subtitle-1 font-weight-medium">
                                                    Sheet: <span class="primary--text">{{ preview_data.import_definition.sheet_name || 'Not specified' }}</span>
                                                </div>
                                                <v-spacer></v-spacer>
                                                <div class="grey--text text--darken-1 mr-4">
                                                    Header row: {{ preview_data.import_definition.has_header_row ? 'Yes' : 'No' }}
                                                </div>
                                                <div class="grey--text text--darken-1">
                                                    {{ preview_data.total_records || 0 }} rows
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </v-card>
                                </div>

                                <!-- Full Preview Table -->
                                <ConsentImportPreviewTable :preview-data="preview_data" :title="preview_table_title"
                                    :show-summary="true" :items-per-page="10" :hide-footer="false" 
                                    :auto-filter-to-errors="has_errors_preventing_import" />
                            </div>

                            <!-- Step 2 Navigation -->
                            <v-card-actions style="padding: 0; margin-top: 20px;">


                                <v-spacer></v-spacer>

                                <v-btn color="secondary" v-on:click="cancelImport"
                                    v-if="preview_ready && can_cancel_import && has_write_access">
                                    Cancel Import
                                    <v-icon right>mdi-cancel</v-icon>
                                </v-btn>
                                <v-btn color="primary" v-on:click="goToStep(4)" :disabled="!can_proceed_to_attestation"
                                    v-if="preview_ready">
                                    Next: Legal Attestation
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </v-stepper-content>
                        <!-- Step 4: Legal Attestation -->
                        <v-stepper-content step="4">
                            <div v-if="preview_ready">
                                <v-card class="elevation-2" style="margin-top: 20px;">
                                    <v-card-title>
                                        <v-icon left>mdi-gavel</v-icon>
                                        Legal Attestation Required
                                    </v-card-title>

                                    <v-card-text>
                                        <v-alert type="info" style="margin-bottom: 20px;">
                                            <strong>Legal Requirement:</strong> You must attest that you have proper legal authority to import
                                            these records. <strong>There is no rollback or undo available after submitting this import.</strong>
                                        </v-alert>

                                        <div style="margin-bottom: 20px;">
                                            <h4>Import Summary</h4>
                                            <v-row>
                                                <v-col v-for="summary in consentSummaryItems" :key="summary.key" :cols="summary.cols || 6">
                                                    <strong>{{ summary.label }}:</strong> {{ getSummaryValue(summary.key, summary.formatter) }}
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <v-divider style="margin: 20px 0;"></v-divider>

                                        <div style="margin-bottom: 20px;">
                                            <h4>Legal Attestation</h4>
                                            <p>By proceeding with this import, you attest that:</p>
                                            <ul style="margin-left: 20px; margin-bottom: 15px;">
                                                <li v-for="requirement in consentLegalRequirements" :key="requirement">
                                                    {{ requirement }}
                                                </li>
                                            </ul>
                                        </div>

                                        <m-legal-agreement
                                            type="consent-import-attestation"
                                            :organization_id="$route.params.organization_id"
                                            :organization_label="organizationLabel"
                                            v-model="attestation_checked"
                                            ref="consent_attestation"
                                            :required="true"
                                        />
                                    </v-card-text>
                                </v-card>
                            </div>

                            <!-- Step 4 Navigation -->
                            <v-card-actions style="padding: 0; margin-top: 20px;" v-if="!importing">
                                <v-btn color="secondary" v-on:click="goToStep(3)" outlined>
                                    <v-icon left>mdi-arrow-left</v-icon>
                                    Back to Preview
                                </v-btn>

                                <v-spacer></v-spacer>
                                <v-btn color="secondary" v-on:click="cancelImport"
                                    v-if="can_cancel_import && has_write_access">
                                    Cancel Import
                                    <v-icon right>mdi-cancel</v-icon>
                                </v-btn>
                                <v-btn color="primary" :disabled="!canProceedToExecution" @click="onCompleteImport"
                                    :loading="importing" style="margin-right: 10px;">
                                    Submit Import...
                                </v-btn>


                            </v-card-actions>
                        </v-stepper-content>

                        <!-- Step 5: Import Execution -->
                        <v-stepper-content step="5">
                            <!-- Import Execution Progress -->
                            <ImportProcessProgress v-if="importing && import_process_id" title="Executing Import..."
                                description="Please wait while we import your consent data into the system. This can take several minutes."
                                :process-id="import_process_id" import-type="consent"
                                :organization-id="$route.params.organization_id"
                                default-status-message="Processing your import..." @complete="onExecutionComplete"
                                @failed="onExecutionFailed" />
                            <div v-else-if="execution_errors.length > 0">
                                <v-alert type="error" style="margin-bottom: 20px;">
                                    <div><strong>Import Failed Unexpectedly</strong></div>
                                    <div v-for="error in execution_errors" :key="error">{{ error }}</div>
                                </v-alert>
                                <v-btn color="secondary" v-on:click="startNewUploadInNewTab">
                                    <v-icon left>mdi-upload</v-icon>
                                    Start New Upload
                                </v-btn>
                            </div>

                            <div v-else style="text-align: center; padding: 40px 0;">
                                <v-icon color="success" size="80">mdi-check-circle</v-icon>

                                <h3 class="success--text" style="margin-top: 20px;">
                                    Import Completed Successfully!
                                </h3>

                                <p style="margin-top: 10px; color: #666;">
                                    Your consent records have been imported successfully.
                                    <span v-if="import_meta_data && import_meta_data.records_imported">
                                        {{ import_meta_data.records_imported }} records were imported.
                                    </span>
                                </p>

                                <!-- Navigation Options -->
                                <div style="margin-top: 30px;">
                                    <h4 style="margin-bottom: 20px; color: #333;">What would you like to do next?
                                    </h4>

                                    <div
                                        style="display: flex; flex-direction: column; gap: 15px; max-width: 600px; margin: 0 auto;">

                                        <!-- Go to Consent Register -->
                                        <v-btn color="primary" large @click="goToConsentRegister"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-shield-check</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">Go to Consent Register</div>
                                                <div style="font-size: 12px; opacity: 0.8;">View all consent records
                                                    for this organization</div>
                                            </div>
                                        </v-btn>

                                        <!-- Go to Import History -->
                                        <v-btn color="secondary" large outlined @click="goToImportHistory"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-history</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">View Import History</div>
                                                <div style="font-size: 12px; opacity: 0.8;">See all consent imports
                                                    for this organization</div>
                                            </div>
                                        </v-btn>

                                        <!-- Import More Records -->
                                        <v-btn color="grey darken-1" large outlined @click="startNewImport"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-plus</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">Import More Records</div>
                                                <div style="font-size: 12px; opacity: 0.8;">Start a new consent
                                                    import</div>
                                            </div>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-stepper-content>
                    </v-stepper-items>
                </v-stepper>
                </div>
            </v-card-text>
        </v-container>
    </m-pageframe>
</template>

<script>
import ConsentImportPreviewTable from '@/components/consent/consent-import-preview-table.vue';
import ColumnMappingComponent from '@/components/common/column-mapping-component.vue';
import ImportProcessProgress from '@/components/common/import-process-progress.vue';
import ConsolidatedImportMixin from '@/mixins/imports/consolidated-import-mixin.js';

export default {
    mixins: [ConsolidatedImportMixin],

    components: {
        ConsentImportPreviewTable,
        ColumnMappingComponent,
        ImportProcessProgress
    },

    data() {
        return {
            // Consent-specific data (common data is provided by consolidated mixin)
            attestation_checked: false,


            // Validation rules
            file_rules: [
                v => !!v || 'File is required',
                v => !v || v.size < 5242880 || `File size should be less than 5 MB`,
                v => !v || v.name.toLowerCase().endsWith('.xlsx') || 'File must be Excel format (.xlsx)'
            ],
        }
    },

    computed: {
        // Required by ConsolidatedImportMixin
        import_type() {
            return 'consent';
        },

        can_upload_file() {
            return this.excel_file && !this.uploading;
        },

        requiredFields() {
            return [
                {
                    key: 'phone_number',
                    label: 'Phone Number',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing phone numbers'
                },
                {
                    key: 'consent_date',
                    label: 'Consent Date',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing consent dates'
                },
                {
                    key: 'specimen',
                    label: 'Specimen',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing consent specimen information'
                }
            ];
        },

        optionalFields() {
            return [
                {
                    key: 'full_name',
                    label: 'Full Name',
                    multiple: false,
                    required: false,
                    hint: 'Select the column containing customer names'
                },
                {
                    key: 'conversational',
                    label: 'Conversational',
                    multiple: false,
                    required: false,
                    hint: 'Select the column for conversational consent (grant/deny/empty)'
                },
                {
                    key: 'informational',
                    label: 'Informational',
                    multiple: false,
                    required: false,
                    hint: 'Select the column for informational consent (grant/deny/empty)'
                },
                {
                    key: 'promotional',
                    label: 'Promotional',
                    multiple: false,
                    required: false,
                    hint: 'Select the column for promotional consent (grant/deny/empty)'
                }
            ];
        },

        canContinueToPreview() {
            return this.can_continue_to_preview && this.column_mapping_data && !this.saving_column_mapping;
        },

        can_proceed_to_attestation() {
            if (!this.preview_data) return false;

            // Cannot proceed if import is cancelled or failed
            if (this.import_state === 'cancelled' || this.import_state === 'failed') {
                return false;
            }

            // Cannot proceed if user doesn't have write access
            if (!this.has_write_access) {
                return false;
            }

            // Cannot proceed if there are ANY invalid rows
            const has_valid_records = (this.preview_data.valid_records_count || 0) > 0;
            const has_invalid_rows = this.has_errors_preventing_import;

            return has_valid_records && !has_invalid_rows;
        },

        canProceedToExecution() {
            // Must be able to proceed to attestation first
            if (!this.can_proceed_to_attestation) return false;

            // Must have attestation checked
            return this.attestation_checked;
        },

        has_errors_preventing_import() {
            if (!this.preview_data) return false;

            // Prevent import if there are ANY invalid rows
            const total_records = this.preview_data.total_records || 0;
            const valid_records = this.preview_data.valid_records_count || 0;
            const has_invalid_rows = total_records > valid_records;

            // Also check for validation errors at the file level
            const has_validation_errors = this.preview_data.validation_errors &&
                this.preview_data.validation_errors.length > 0;

            return has_invalid_rows || has_validation_errors;
        },

        canProceedToPreview() {
            const allowedStates = ['preview-ready', 'failed', 'cancelled'];
            return this.import_id && (allowedStates.includes(this.import_state) || this.preview_ready);
        },

        
        //Override the default for consent imports so that RedChirp staff can not write to real orgs
        has_write_access() {
            const organization_id = this.$route.params.organization_id;

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                // RedChirp staff can only write to test/demo organizations
                return this.$rc.$state.isDemoOrTestOrganization();
            } else if (this.$rc.$state.isOrganizationAdmin(organization_id)) {
                // Organization admins can write to their organization
                return true;
            }

            // Regular users cannot write
            return false;
        },

        canEditStep() {
            return (step) => {
                // Simplified step navigation logic
                if (this.import_locked) {
                    return false;
                }
                const isCompleted = this.import_state === 'completed';

                switch (step) {
                    case 1:
                        return false; // Can't go back to upload once started
                    case 2:
                        // Can only edit column mapping if we're in uploaded state and haven't proceeded to preview yet
                        return this.import_state === 'uploaded' && this.sheet_information && this.current_step <= 2;
                    case 3:
                        // Allow navigating to preview immediately after mapping is saved
                        // so the "Generating Preview..." loader can show while data loads
                        return this.canContinueToPreview && !isCompleted;
                    case 4:
                        return this.can_proceed_to_attestation && !isCompleted;
                    case 5:
                        return isCompleted;
                    default:
                        return false;
                }
            };
        },

        // Legal attestation computed properties (moved from LegalAttestationComponent)
        organizationLabel() {
            return this.$route.params.organization_label || 'your organization';
        },

        consentSummaryItems() {
            return [
                { key: 'conversational_count', label: 'Conversational Consent Records', cols: 3 },
                { key: 'informational_count', label: 'Informational Consent Records', cols: 3 },
                { key: 'promotional_count', label: 'Promotional Consent Records', cols: 3 }
            ];
        },

        consentLegalRequirements() {
            return [
                'You have proper legal authority to import these consent records',
                'All consent records were obtained in compliance with applicable laws including TCPA',
                'You have verified the accuracy of the consent information',
                'You understand this action will update customer consent preferences',
                'Each individual has provided explicit written consent to receive text messages'
            ];
        }
    },

    mounted() {
        this.initializeComponent();
    },

    methods: {



        // Override mixin method for consent-specific navigation
        goBackToImportSearch() {
            this.$router.push({
                name: 'search-consent-imports',
                params: { organization_id: this.$route.params.organization_id }
            });
        },

        goToImportHistory() {
            this.$router.push({
                name: 'search-consent-imports',
                params: { organization_id: this.$route.params.organization_id }
            });
        },

        onClickDownloadTemplate() {
            // Use generic import endpoint
            const download_url = this.$rc.getAPIEndpoint('/consent/download-import-template');
            window.open(download_url, '_blank');
        },

        getStateLabel(state) {
            const stateLabels = {
                'uploaded': 'File Uploaded',
                'previewing': 'Generating Preview',
                'preview-ready': 'Preview Ready',
                'importing': 'Importing',
                'completed': 'Completed',
                'failed': 'Failed - Cannot Import',
                'cancelled': 'Cancelled - Cannot Import'
            };
            return stateLabels[state] || state;
        },


        onCanContinueChanged(canContinue) {
            this.can_continue_to_preview = canContinue;
        },

        // Override mixin method for consent-specific step navigation
        goToStep(step) {
            // Validate that we can navigate to the requested step
            if (!this.canEditStep(step)) {
                return;
            }

            this.current_step = step;

            // Handle any step-specific logic for consent imports
            switch (step) {
                case 2:
                    // Column mapping step - load sheet information if needed
                    if (this.import_id && !this.sheet_information && !this.loading_sheet_information) {
                        this.loadSheetInformation();
                    }
                    break;
                case 3:
                    // Preview step - start preview generation if needed
                    if (this.import_id && !this.preview_ready && !this.generating_preview && !this.preview_failed) {
                        this.startPreviewGeneration();
                    }
                    break;
                case 4:
                    // Ensure we have valid preview data for attestation
                    if (!this.can_proceed_to_attestation) {
                        this.current_step = 3;
                        return;
                    }
                    break;
                case 5:
                    // Completion step - no specific logic needed
                    break;
            }
        },

        onContinueToPreview() {
            // Trigger column mapping save directly
            this.saveColumnMapping();
        },

        // === COLUMN MAPPING API METHODS ===

        // Column mapping methods are now provided by ConsolidatedImportMixin

        // Override ConsolidatedImportMixin to handle consent-specific state transitions
        handleImportStateTransition() {
            this.import_locked = false;
            this.import_locked_reason = '';

            switch (this.import_state) {
                case 'uploading':
                    this.current_step = 1;
                    this.uploading = false;
                    this.upload_errors = ['Upload process was interrupted. Please try uploading the file again.'];
                    break;

                case 'uploaded':
                    this.current_step = 2;
                    if (!this.sheet_information && !this.loading_sheet_information) {
                        this.loadSheetInformation();
                    }
                    break;

                case 'preview-ready':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;

                case 'importing':
                    this.applyImportInProgressState();
                    this.execution_errors = [];
                    break;

                case 'completed':
                    this.current_step = 5;
                    this.importing = false;
                    break;

                case 'cancelled':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;

                case 'failed':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;
            }
        },

        // Override mixin methods for consent-specific behavior
        onMappingSaved(data) {
            //this.handleImportStateTransition();
            this.goToStep(3);
        },

        onMappingFailed(data) {
            //this.handleImportStateTransition();
            this.goToStep(3);
        },

        // Override cancel import to add confirmation dialog
        cancelImport() {
            this.$rc.confirm({
                title: 'Cancel Import',
                body: 'Are you sure you want to cancel this consent import? This action cannot be undone and the import will be marked as cancelled.',
                btn_text_ok: 'Yes, Cancel Import',
                btn_text_cancel: 'Keep Import',
                on_ok: () => {
                    this.$rc.rest({
                        'spinner': true,
                        'endpoint': `POST: /api/v1/imports/${this.import_type}/cancel/${this.$route.params.organization_id}/${this.import_id}`,
                        'params': {},
                        'on_success': () => {
                            this.import_state = 'cancelled';
                            this.handleImportStateTransition();
                        },
                        'on_error': (error) => {
                            this.$rc.alert('Failed to cancel import', error);
                        }
                    });
                }
            });
        },



        onCompleteImport() {
            // Require explicit confirmation before proceeding
            this.$rc.confirm({
                title: 'Final Confirmation Required',
                body: `<div style="margin-bottom: 16px;">
                    <strong>⚠️ IMPORTANT: This action cannot be undone!</strong>
                </div>
                <div style="margin-bottom: 16px;">
                    You are about to execute the consent import. Once started, this process cannot be stopped or rolled back.
                    All consent records will be permanently added to your system.
                </div>`,
                btn_text_ok: 'Execute Import',
                btn_text_cancel: 'Cancel',
                text_to_confirm: 'Confirm',
                text_to_confirm_message: 'To proceed, please type "Confirm" in the box below:',
                on_ok: () => {
                    this.current_step = 5; // Move to execution step
                    this.onExecuteImport(); // Use mixin method
                }
            });
        },

        // Override mixin method to provide consent-specific request parameters
        getExecutionRequestParams() {
            const attestation_component = this.$refs.consent_attestation;
            const specimen = attestation_component ? attestation_component.getSpecimen() : null;

            if (!specimen) {
                throw new Error('Legal attestation specimen missing, please agree to the attestation to continue.');
            }

            return {
                legal_attestation_specimen: specimen
            };
        },
        goToConsentRegister() {
            this.$router.push({
                name: 'search-consent-register',
                params: {
                    organization_id: this.$route.params.organization_id
                }
            });
        },

        startNewImport() {
            const route = this.$router.resolve({
                name: 'consent-import-upload',
                params: { organization_id: this.$route.params.organization_id }
            });
            window.location.href = route.href;
        },

        startNewUploadInNewTab() {
            // Open a new tab with the consent import upload page
            const route = this.$router.resolve({
                name: 'consent-import-upload',
                params: {
                    organization_id: this.$route.params.organization_id
                }
            });
            window.open(route.href, '_blank');
        },

        getSummaryValue(key, formatter) {
            const value = this.preview_data?.[key] || 0;

            if (formatter && typeof formatter === 'function') {
                return formatter(value, this.preview_data);
            }

            return value;
        }


    }
};

</script>

<style lang="scss" scoped></style>
